import React, { useContext, useEffect, useRef, useState } from 'react';
import { makeStyles, Theme, createStyles } from '@material-ui/core/styles';
import { Modal, Grid, Typography, TextField } from '@material-ui/core';
import { useForm, Controller } from 'react-hook-form';
import { useMsal } from '@azure/msal-react';
import { AccountInfo } from '@azure/msal-common';
import Button from '../../../elements/Button';
import { ClientConfigContext } from '../../../config/clientConfig';
import { ApiStatus, getMrnData, linkRecord } from '../api';
import { MrnData } from '../../../types/api';
import { SessionContext } from '../../../session/sessionContext';
import { SearchOptions } from '../../../types/front-end';

export type AddLinkedRecordModalProps = {
    open: boolean;
    handleClose: () => void;
    healthSystemId: string;
    azureUserId: string;
    searchMethod: (opts: SearchOptions) => Promise<void>;
    searchTerm: string;
    /** EHR systems already linked to this account as a patient or NPP. */
    existingLinkedSystems: string[];
};

// Center the modal within the viewport.
function getModalStyle() {
    const top = 50;
    const left = 50;

    return {
        top: `${top}%`,
        left: `${left}%`,
        transform: `translate(-${top}%, -${left}%)`,
    };
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        paper: {
            position: 'absolute',
            width: 448,
            height: 344,
            backgroundColor: theme.palette.background.paper,
            borderRadius: '24px',
            boxShadow: theme.shadows[5],
        },
    }),
);

/** Look up an MRN and confirm its patient link in the EHR discovered by PAS. */
function AddLinkedRecordModal({ open, handleClose, healthSystemId, azureUserId, searchMethod, searchTerm, existingLinkedSystems }: AddLinkedRecordModalProps) {
    const msalContext = useMsal();
    const { instance } = msalContext;
    const accounts = msalContext.accounts as AccountInfo[];
    const clientConfiguration = useContext(ClientConfigContext);
    const sessionId = useContext(SessionContext);
    const classes = useStyles();
    const [modalStyle] = useState(getModalStyle);
    const {
        handleSubmit,
        control,
        reset,
        formState: { errors, isDirty, isValid },
    } = useForm<{ mrn: string }>({ mode: 'onChange' });
    const [mrnDetails, setMrnDetails] = useState<MrnData>();
    const [lookedUpMrn, setLookedUpMrn] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [lookupInProgress, setLookupInProgress] = useState(false);
    const [linkInProgress, setLinkInProgress] = useState(false);
    const hasExistingEhrLink = mrnDetails ? existingLinkedSystems.includes(mrnDetails.ehrSystem) : false;
    const isSubmissionDisabled = !isDirty || !isValid || lookupInProgress || linkInProgress;

    // Requests capture this revision; MRN edits and account/modal resets make their responses stale.
    const lookupRevision = useRef(0);
    const isModalOpen = useRef(open);
    isModalOpen.current = open;

    // Clear patient details and prevent pending responses from restoring them.
    const invalidateLookup = () => {
        lookupRevision.current += 1;
        setMrnDetails(undefined);
        setErrorMessage('');
        setLookupInProgress(false);
    };

    // Start fresh when the modal is reopened or the selected SSO account changes.
    useEffect(() => {
        invalidateLookup();
        setLookedUpMrn('');
        reset({ mrn: '' });
        return () => {
            lookupRevision.current += 1;
        };
    }, [open, healthSystemId, azureUserId, reset]);

    // Invalidate requests immediately, before the parent updates the open prop.
    const closeModal = () => {
        invalidateLookup();
        isModalOpen.current = false;
        handleClose();
    };

    // Look up the MRN and accept only the current response with its discovered EHR.
    const handleMrnLookup = async (data: { mrn: string }) => {
        invalidateLookup();
        const normalizedMrn = data.mrn.trim();
        setLookedUpMrn(normalizedMrn);
        const requestRevision = lookupRevision.current;
        setLookupInProgress(true);
        const result = await getMrnData(normalizedMrn, instance, accounts, clientConfiguration, sessionId);
        if (!isModalOpen.current || requestRevision !== lookupRevision.current) {
            return;
        }
        setLookupInProgress(false);
        if (result.status === ApiStatus.Success && result.data?.ehrSystem) {
            setMrnDetails(result.data);
            if (existingLinkedSystems.includes(result.data.ehrSystem)) {
                setErrorMessage('There is already an MRN linked to this account from this region.');
            }
        } else {
            setErrorMessage(result.status === ApiStatus.Error && result.error ? result.error.message : 'Something went wrong, try again later.');
        }
    };

    // Link using the EHR returned by the confirmed MRN lookup.
    const handleLinkRecord = async () => {
        if (!mrnDetails?.myChartLogin) {
            setErrorMessage('MyChart account does not exist');
            return;
        }
        if (linkInProgress || hasExistingEhrLink) {
            return;
        }
        const matchedEhrSystem = mrnDetails.ehrSystem;
        setLinkInProgress(true);
        const requestRevision = lookupRevision.current;
        try {
            const result = await linkRecord(
                lookedUpMrn,
                matchedEhrSystem,
                mrnDetails.myChartLogin,
                healthSystemId,
                azureUserId,
                instance,
                accounts,
                clientConfiguration,
                sessionId,
            );
            if (!isModalOpen.current || requestRevision !== lookupRevision.current) {
                return;
            }
            if (result.status === ApiStatus.Success) {
                closeModal();
                searchMethod({ searchTerm, refreshCorrelationId: false, region: matchedEhrSystem, healthSystemId });
            } else {
                setErrorMessage(result.status === ApiStatus.Error && result.error ? result.error.message : 'Something went wrong, try again later.');
            }
        } finally {
            setLinkInProgress(false);
        }
    };

    // Show the patient details associated with the successful MRN lookup.
    const renderSearchResults = () => {
        if (mrnDetails) {
            return (
                <Grid container item xs={12} className='mrnSearchResults'>
                    <Grid item xs={12}>
                        <Typography variant='subtitle1'>{mrnDetails.name}</Typography>
                    </Grid>
                    <Grid container item xs={4}>
                        <Grid item xs={12}>
                            <Typography variant='subtitle1'>MRN</Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <Typography variant='subtitle2'>{lookedUpMrn}</Typography>
                        </Grid>
                    </Grid>
                    <Grid container item xs={8}>
                        <Grid item xs={12}>
                            <Typography variant='subtitle1'>MyChart Username</Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <Typography variant='subtitle2'>{mrnDetails.myChartLogin}</Typography>
                        </Grid>
                    </Grid>
                </Grid>
            );
        } else {
            return <Grid item xs={12} className='mrnSearchResults'></Grid>;
        }
    };

    // Reserve space for lookup and linking errors beneath the patient details.
    const renderErrorMessage = () => {
        return (
            <Grid item xs={12} style={{ marginRight: 20 }}>
                <Typography variant='subtitle1' color='error' align='right'>
                    {errorMessage || <>&nbsp;</>}
                </Typography>
            </Grid>
        );
    };

    // Offer confirmation only after a successful lookup without an error.
    const renderLinkRecordButton = () => {
        if (mrnDetails && !errorMessage) {
            return (
                <>
                    <Grid item md={2} xs={1} />
                    <Grid item md={3} xs={4}>
                        <Button
                            disabled={isSubmissionDisabled}
                            type='button'
                            variant='contained'
                            color='secondary'
                            onClick={handleLinkRecord}
                            fontSize='14px'
                            heightOverride='28px'
                            widthOverride='200px'
                        >
                            Link Record
                        </Button>
                    </Grid>
                </>
            );
        } else {
            return <></>;
        }
    };

    const body = (
        <form onSubmit={handleSubmit(handleMrnLookup)} style={modalStyle} className={classes.paper}>
            <Grid container item xs={12} data-testid='searchGrid'>
                <Grid item xs={12} style={{ height: '44px', marginLeft: '20px', marginTop: '10px' }}>
                    <Typography variant='h3'>Search for account</Typography>
                </Grid>
                <Grid item xs={12} style={{ height: '0px', border: '1px solid #D2D2D2' }} />
                <Grid container alignItems='center' item xs={8} style={{ height: '62px', marginLeft: '20px' }}>
                    <Typography variant='subtitle2'>This will grant access to the patient’s records to this SSO account.</Typography>
                </Grid>
                <Grid container item xs={12} direction='row' spacing={2} alignItems='center'>
                    <Grid item md={6} sm={7} xs={7}>
                        <Controller
                            name='mrn'
                            control={control}
                            rules={{ required: true }}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    disabled={linkInProgress}
                                    onChange={(event) => {
                                        invalidateLookup();
                                        field.onChange(event);
                                    }}
                                    className='mrnSearchControls'
                                    variant='outlined'
                                    id='mrn'
                                    autoComplete='off'
                                    fullWidth
                                    error={errors.mrn?.type === 'required'}
                                    helperText={errors.mrn?.type === 'required' && 'MRN required'}
                                    placeholder='Search MRN'
                                    InputLabelProps={{
                                        shrink: true,
                                        error: false,
                                    }}
                                />
                            )}
                        />
                    </Grid>
                    <Grid container alignItems='center' item md={2} sm={2} xs={2} className='mrnSearchControls'>
                        <Button disabled={isSubmissionDisabled} type='submit' variant='contained' color='primary' fontSize='16px'>
                            Search
                        </Button>
                    </Grid>
                </Grid>
                <Grid item xs={12}>
                    <hr className='linkedRecordModalDividerLine' />
                </Grid>
                {renderSearchResults()}
                {renderErrorMessage()}
                <Grid item md={3} xs={4} style={{ marginLeft: '20px', marginRight: '28px' }}>
                    <Button type='button' variant='outlined' color='secondary' fontSize='14px' onClick={closeModal} heightOverride='28px' widthOverride='96px'>
                        Cancel
                    </Button>
                </Grid>
                {renderLinkRecordButton()}
            </Grid>
        </form>
    );

    return (
        <Modal open={open} onClose={closeModal}>
            {body}
        </Modal>
    );
}

export default AddLinkedRecordModal;
